import React from "react";
import { useQuery } from "@tanstack/react-query";
import { usePageSeo } from "../lib/usePageSeo";
import { Leaf, FlaskConical, ShieldCheck, Award } from "lucide-react";
import { LIFESTYLE_RUN } from "../lib/constants";
import { fetchProduct } from "../lib/api";
import { useFormulation } from "../components/formulation/useFormulation";
import ComboSection from "../components/formulation/ComboSection";
import ActivesSection from "../components/formulation/ActivesSection";
import FormulationExplorer from "../components/formulation/FormulationExplorer";

const PILLARS = [
    { icon: Leaf, t: "Natural Actives", d: "Premium botanicals + adaptogens" },
    { icon: FlaskConical, t: "Scientifically Formulated", d: "Clinically-studied extracts" },
    { icon: ShieldCheck, t: "Quality Assured", d: "Tested for purity & potency" },
    { icon: Award, t: "Made in India", d: "FSSAI-licensed manufacturing" },
];

export default function Science() {
    usePageSeo({
        title: "The Science — Unani, Ayurveda & Nutrition | ReHealthMe",
        description: "How ReHealthMe formulates: Unani and Ayurvedic tradition read alongside modern nutrition science, with every active, dose and reference set out plainly.",
        canonical: `${window.location.origin}/science`,
    });

    const { data: product } = useQuery({
        queryKey: ["product", "redrive-max"],
        queryFn: () => fetchProduct("redrive-max"),
    });
    const form = useFormulation(product);

    return (
        <div className="pt-28 md:pt-32 pb-20" data-testid="science-page">
            <section className="relative h-[420px] md:h-[520px] overflow-hidden rounded-sm mx-4 md:mx-12">
                <img src={LIFESTYLE_RUN} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg0 via-bg0/70 to-transparent" />
                <div className="relative h-full flex items-end p-8 md:p-14">
                    <div className="max-w-3xl">
                        <div className="text-lime uppercase tracking-[0.3em] text-xs mb-4">Powered by Nature</div>
                        <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tight">
                            Backed by <span className="text-lime">Science</span>.
                        </h1>
                        <p className="mt-4 text-zinc-300 max-w-xl">
                            We combine clinically-studied actives with time-honoured Ayurvedic botanicals — delivered in a
                            fast-absorbing effervescent for maximum bioavailability.
                        </p>
                    </div>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 md:px-12 pt-16 md:pt-24">
                <div className="grid md:grid-cols-4 gap-4">
                    {PILLARS.map(({ icon: Icon, t, d }) => (
                        <div key={t} className="p-6 rounded-sm border border-white/10 bg-bg1">
                            <Icon className="w-6 h-6 text-lime mb-3" />
                            <div className="font-display text-lg uppercase tracking-tight">{t}</div>
                            <div className="text-xs text-zinc-400 mt-1">{d}</div>
                        </div>
                    ))}
                </div>
            </section>

            {form.enabled ? (
                <>
                    <ComboSection sections={form.sections} combos={form.combos} byCombo={form.byCombo} basisLabel={form.basisLabel} />
                    <ActivesSection sections={form.sections} actives={form.actives} comboMap={form.comboMap} trademarks={form.trademarks} />
                    <FormulationExplorer sections={form.sections} combos={form.combos} byCombo={form.byCombo}
                        comboMap={form.comboMap} basisLabel={form.basisLabel} />
                </>
            ) : (
                <div className="mx-auto max-w-7xl px-6 md:px-12 py-20 text-zinc-500">Loading formulation…</div>
            )}
        </div>
    );
}
